import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/ContactEditPage.css";
import Breadcrumb from "./Breadcrumb";
import { useAuth } from "../context/AuthContext";
import { useMatter } from "../context/MatterContext";
import { patchBhpClientContact } from "../helpers/apiHelper";
import { ContactValidators } from "../helpers/validationHelper";

const readClientId = () =>
  localStorage.getItem("clientId") || sessionStorage.getItem("clientId") || "";

export default function ContactEditPage() {
  const navigate = useNavigate();
  const { tokens, applyRefreshedTokens } = useAuth();
  const { party, setParty } = useMatter();

  const storedClientId = useMemo(() => readClientId(), []);
  const partyId =
    party?.partyId || party?.id || party?.Id || storedClientId || "";

  useEffect(() => {
    if (!party) navigate("/multimatters", { replace: true });
  }, [party, navigate]);

  // Original values from the party record, used both to prepopulate the
  // fields and to detect whether the user actually changed anything.
  const initialEmail = useMemo(() => party?.email || "", [party]);
  const initialPhone = useMemo(() => party?.phone || "", [party]);
  const maskedPixKey = party?.bhprPixKey || "";

  const initialNormalizedEmail = useMemo(
    () => ContactValidators.normalizeEmail(initialEmail),
    [initialEmail],
  );
  const initialNormalizedPhoneE164 = useMemo(
    () => ContactValidators.formatPhoneE164BR(initialPhone) || initialPhone,
    [initialPhone],
  );

  const [email, setEmail] = useState(initialEmail);
  const [phone, setPhone] = useState(initialPhone);
  const [pixType, setPixType] = useState("");
  const [pixKey, setPixKey] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const emailProvided = email.trim().length > 0;
  const phoneProvided = phone.trim().length > 0;
  const pixTypeTouched = pixType.trim().length > 0;
  const pixKeyTouched = pixKey.trim().length > 0;

  const normalizedEmail = useMemo(() => {
    if (!emailProvided) return "";
    return ContactValidators.normalizeEmail(email);
  }, [email, emailProvided]);

  const emailOk = useMemo(() => {
    if (!emailProvided) return true;
    return ContactValidators.isValidEmail(normalizedEmail);
  }, [emailProvided, normalizedEmail]);

  const emailChanged =
    emailProvided && emailOk && normalizedEmail !== initialNormalizedEmail;

  const normalizedPhoneE164 = useMemo(() => {
    if (!phoneProvided) return "";
    return ContactValidators.formatPhoneE164BR(phone);
  }, [phone, phoneProvided]);

  const phoneOk = useMemo(() => {
    if (!phoneProvided) return true;
    return Boolean(normalizedPhoneE164);
  }, [phoneProvided, normalizedPhoneE164]);

  const phoneChanged =
    phoneProvided &&
    phoneOk &&
    normalizedPhoneE164 !== initialNormalizedPhoneE164;

  const pixNeedsValidation = pixTypeTouched || pixKeyTouched;

  const normalizedPix = useMemo(() => {
    if (!pixNeedsValidation) return "";
    return ContactValidators.normalizePixKeyByType(pixType, pixKey);
  }, [pixNeedsValidation, pixType, pixKey]);

  const pixValidation = useMemo(() => {
    if (!pixNeedsValidation) return { ok: true, value: "" };
    return ContactValidators.validatePixKeyByType(pixType, normalizedPix);
  }, [pixNeedsValidation, pixType, normalizedPix]);

  const pixPairOk = useMemo(() => {
    if (!pixNeedsValidation) return true;
    if (!pixTypeTouched || !pixKeyTouched) return false;
    return pixValidation.ok;
  }, [pixNeedsValidation, pixTypeTouched, pixKeyTouched, pixValidation.ok]);

  // PIX key comes back masked from the backend and we don't know its type,
  // so any input there is always treated as a brand new value (there's no
  // baseline to diff against).
  const nothingToUpdate = !emailChanged && !phoneChanged && !pixNeedsValidation;

  const canSubmit =
    !submitting &&
    Boolean(partyId) &&
    !nothingToUpdate &&
    emailOk &&
    phoneOk &&
    pixPairOk;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!canSubmit) return;

    const token =
      tokens?.idToken ||
      tokens?.accessToken ||
      localStorage.getItem("idToken") ||
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("idToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) {
      setErrorMsg("Sessão expirada. Faça login novamente.");
      return;
    }

    const cpf = party?.cpf || "";

    if (!cpf) {
      setErrorMsg(
        "Não foi possível identificar seu CPF. Faça login novamente.",
      );
      return;
    }

    const payload = { cpf };

    if (emailChanged) payload.email = normalizedEmail;
    if (phoneChanged) payload.phone = normalizedPhoneE164;
    if (pixNeedsValidation) payload.pixKey = pixValidation.value;

    setSubmitting(true);

    try {
      const patchResult = await patchBhpClientContact(token, partyId, payload);
      // Email/phone changes invalidate the current Cognito refresh token; use the fresh pair
      // the backend reissues in the same response so this session survives instead of being
      // force-logged-out on the next scheduled refresh.
      if (emailChanged || phoneChanged) {
        applyRefreshedTokens(patchResult);
      }

      setParty((prev) => ({
        ...(prev || {}),
        ...(emailChanged ? { email: normalizedEmail } : {}),
        ...(phoneChanged ? { phone: normalizedPhoneE164 } : {}),
        ...(pixNeedsValidation ? { bhprPixKey: pixValidation.value } : {}),
      }));

      navigate(-1);
    } catch (err) {
      setErrorMsg(err?.message || "Não foi possível atualizar seus dados.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="main-wrapper">
      <Breadcrumb
        items={[
          { label: "Perfil", link: "/profile" },
          { label: "Atualizar contato" },
        ]}
      />

      <div className="cePage">
        <section className="ceCard">
          <div className="ceHeader">
            <h1 className="ceTitle">Atualizar informações de contato</h1>
            <p className="ceSubtitle">
              Seus dados atuais já estão preenchidos. Altere apenas o que
              deseja atualizar.
            </p>
          </div>

          <form className="ceForm" onSubmit={handleSubmit}>
            <div className="ceField">
              <label className="ceLabel" htmlFor="ce-email">
                Email
              </label>
              <input
                id="ce-email"
                className="ceInput"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              {!emailOk && emailProvided ? (
                <div className="ceError">E-mail inválido.</div>
              ) : null}
            </div>

            <div className="ceField">
              <label className="ceLabel" htmlFor="ce-phone">
                Número de Telefone
              </label>
              <input
                id="ce-phone"
                className="ceInput"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
              />
              {!phoneOk && phoneProvided ? (
                <div className="ceError">
                  Telefone inválido (use DDD + número).
                </div>
              ) : null}
            </div>

            <div className="ceField">
              <label className="ceLabel" htmlFor="ce-pix-type">
                Chave PIX
              </label>

              {maskedPixKey ? (
                <div className="ceHint">
                  Chave atual: {maskedPixKey}. Preencha abaixo apenas se
                  quiser substituí-la.
                </div>
              ) : null}

              <div className="ceSelectWrap">
                <select
                  id="ce-pix-type"
                  className="ceSelect"
                  value={pixType}
                  onChange={(e) => setPixType(e.target.value)}
                >
                  <option value="">Selecione o tipo PIX</option>
                  <option value="CPF">CPF</option>
                  <option value="CNPJ">CNPJ</option>
                  <option value="Telefone">Telefone</option>
                  <option value="E-mail">E-mail</option>
                  <option value="Chave Aleatória">Chave Aleatória</option>
                </select>
                <span className="ceChevron" aria-hidden="true">
                  ⌄
                </span>
              </div>

              <input
                className="ceInput"
                type="text"
                value={pixKey}
                onChange={(e) => setPixKey(e.target.value)}
              />

              {pixNeedsValidation && (!pixTypeTouched || !pixKeyTouched) ? (
                <div className="ceError">
                  Selecione o tipo PIX e informe a chave.
                </div>
              ) : null}

              {pixNeedsValidation &&
              pixTypeTouched &&
              pixKeyTouched &&
              !pixValidation.ok ? (
                <div className="ceError">{pixValidation.error}</div>
              ) : null}
            </div>

            {errorMsg ? (
              <div className="ceError ceErrorBanner">{errorMsg}</div>
            ) : null}

            <button
              type="submit"
              className={`cePrimaryBtn ${canSubmit ? "isEnabled" : "isDisabled"}`}
              disabled={!canSubmit}
            >
              {submitting ? "Atualizando..." : "Atualizar contato"}
            </button>

            <button
              type="button"
              className="ceCancelBtn"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              Cancelar
            </button>
          </form>
        </section>

        <div className="bottomSpacer" />
      </div>
    </div>
  );
}
